import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { withTheme } from "../theming";
import { useResponsive } from "../contexts/ResponsiveContext";
import { userPool } from "../awsConfig";
import { clearAuthState } from "../utils/authUtils";
import { PUBLIC_NAV_LINKS, AUTH_NAV_LINKS, CONSULT_MAILTO } from "../config/site";

const Header = ({ theme, isAuthenticated, setIsAuthenticated }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { isCompactNav, isPortrait, isPhoneLandscape } = useResponsive();
  // Hamburger on any compact viewport, portrait orientation, or short landscape phones.
  const showHamburger = isCompactNav || isPortrait || isPhoneLandscape;

  // The stark public pages render their own top-nav via StarkShell, so the global
  // Midnight header hides on all of them. It stays only on the authenticated
  // operator surfaces (Agent Composer, etc.) and the auth screens.
  const STARK_ROUTES = new Set([
    "/", "/platform", "/how-we-work", "/proof", "/about",
    "/capabilities", "/feasibility-sprint", "/live", "/built-to-last",
  ]);
  if (STARK_ROUTES.has(location.pathname)) return null;

  // On auth pages, suppress the menu items (same as the x20 siblings).
  const isAuthPage =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/confirm-signup";
  // Authenticated track shows only when logged in and off the auth pages.
  const shouldShowMenuItems = isAuthenticated && !isAuthPage;

  const closeMenu = () => setIsOpen(false);

  const handleLogout = () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) cognitoUser.signOut();
    setIsOpen(false);
    clearAuthState();
    if (setIsAuthenticated) setIsAuthenticated(false);
    window.location.replace("/login");
  };

  const accent = theme.accentColor || "#7e9aae";

  const linkClass = ({ isActive }) =>
    isActive
      ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
      : "text-[120%] hover:opacity-90 focus:opacity-90";
  const mobileLinkClass = ({ isActive }) =>
    `block py-3 px-4 text-[120%] border-b ${isActive ? "font-bold" : ""}`;

  const ctaStyle = {
    backgroundColor: accent,
    color: theme.buttonTextColor || "#fff",
    borderRadius: "50px",
    padding: "6px 20px",
    fontWeight: 600,
    fontSize: "1rem",
    textDecoration: "none",
    whiteSpace: "nowrap",
    transition: "opacity 0.2s ease",
  };

  return (
    <header
      style={{
        backgroundColor: theme.backgroundColor || "#10202c",
        color: theme.textColor || "#fff",
        position: "sticky",
        top: 0,
        zIndex: 1000,
      }}
      className="p-4"
    >
      <div className="mx-auto flex justify-between items-center pr-10 pl-5">
        {/* Logo */}
        <div className="flex-shrink-0 mr-8">
          <NavLink
            to="/"
            className={() => {
              const sizeCls = showHamburger ? "text-[135%]" : "text-[170%]";
              return `${sizeCls} font-bold hover:opacity-90 focus:opacity-90`;
            }}
            style={{ color: accent }}
          >
            X20 Consulting
          </NavLink>
        </div>

        {/* Hamburger */}
        <div className={showHamburger ? "block" : "hidden"}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="focus:outline-none p-2 rounded-md"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-nav"
            style={{ backgroundColor: theme.backgroundColor || "#10202c", color: accent }}
          >
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Desktop menu */}
        <nav className={showHamburger ? "hidden" : "flex space-x-4 items-center"}>
          {shouldShowMenuItems ? (
            <>
              {AUTH_NAV_LINKS.map((link) => (
                <NavLink key={link.path} to={link.path} className={linkClass} style={{ color: accent }}>
                  {link.name}
                </NavLink>
              ))}
              <NavLink to="#" onClick={handleLogout} className={linkClass} style={{ color: accent }}>
                Logout
              </NavLink>
            </>
          ) : (
            <>
              {PUBLIC_NAV_LINKS.map((link) => (
                <NavLink key={link.path} to={link.path} className={linkClass} style={{ color: accent }}>
                  {link.name}
                </NavLink>
              ))}
              <NavLink to="/login" className={linkClass} style={{ color: accent }}>
                Login
              </NavLink>
              <a
                href={CONSULT_MAILTO}
                style={ctaStyle}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                Free Consultation
              </a>
            </>
          )}
        </nav>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <nav
          id="mobile-nav"
          className={`${showHamburger ? "block" : "hidden"} mt-4 max-h-[calc(100vh-80px)] overflow-y-auto`}
          style={{ borderTop: `1px solid ${accent}22` }}
        >
          {shouldShowMenuItems ? (
            <>
              {AUTH_NAV_LINKS.map((link) => (
                <NavLink key={link.path} to={link.path} onClick={closeMenu} className={mobileLinkClass} style={{ color: accent, borderColor: `${accent}1a` }}>
                  {link.name}
                </NavLink>
              ))}
              <NavLink to="#" onClick={handleLogout} className="block py-3 px-4 text-[120%]" style={{ color: accent }}>
                Logout
              </NavLink>
            </>
          ) : (
            <>
              {PUBLIC_NAV_LINKS.map((link) => (
                <NavLink key={link.path} to={link.path} onClick={closeMenu} className={mobileLinkClass} style={{ color: accent, borderColor: `${accent}1a` }}>
                  {link.name}
                </NavLink>
              ))}
              <NavLink to="/login" onClick={closeMenu} className={mobileLinkClass} style={{ color: accent, borderColor: `${accent}1a` }}>
                Login
              </NavLink>
              <a href={CONSULT_MAILTO} onClick={closeMenu} className="block py-3 px-4 text-[120%] font-semibold" style={{ color: accent }}>
                Free Consultation
              </a>
            </>
          )}
        </nav>
      )}
    </header>
  );
};

export default withTheme(Header);
